import { convertNumberToCurrency, convertNumberToUnit } from '../../../helpers/number';
import trans from '../../../helpers/trans';
import {
    Locales,
    ProductAttributeChoices,
    ProductAttributeKey,
    ProductAttributeResource,
    ProductAttributeType,
    ProductDimensionAttributeKey,
} from '../../@api/Sylius';
import { ProductAttribute } from './ProductAttribute';

const locale = Locales.nl;

const sizeAttributes = [
    ProductDimensionAttributeKey.width,
    ProductDimensionAttributeKey.height,
    ProductDimensionAttributeKey.depth,
    ProductDimensionAttributeKey.length,
    ProductDimensionAttributeKey.radius,
    ProductDimensionAttributeKey.cord,
    ProductDimensionAttributeKey.seat,
    ProductDimensionAttributeKey.armrest,
];

const weightAttributes = [
    ProductDimensionAttributeKey.weight,
    ProductDimensionAttributeKey.carryWeight,
];

export const transformToKilograms = (decigrams: number): string => (
    convertNumberToUnit(decigrams / 100, 'kilogram')
);

export const transformToCentimeters = (millimeters: number): string => (
    convertNumberToUnit(millimeters / 100, 'centimeter')
);

export const transformToMilliliters = (centiliters: number): string => (
    convertNumberToUnit(centiliters * 10, 'milliliter')
);

export const transformToWattage = (hectoWatt: number): string => {
    const localizedWattage = (hectoWatt / 100).toLocaleString();

    return `${localizedWattage} W`;
};

export const transformToCurrency = (cents: number): string => convertNumberToCurrency(cents / 100, {
    includeCurrencySymbol: true,
});

const getLocalizedAttributeValues = (choices: ProductAttributeChoices, values: string[]): string[] => (
    values
        .filter(id => id in choices)
        .map(id => choices[id][locale])
);

export const transformToProductAttribute = (resource: ProductAttributeResource): ProductAttribute => {
    const { code, type, value } = resource;
    const { choices } = resource.attribute.configuration;

    const attributeDefaults = {
        key: resource.code,
        label: resource.name,
    };

    if (type === ProductAttributeType.boolean) {
        const booleanLabel = value
            ? trans('common.yes')
            : trans('common.no');

        return {
            ...attributeDefaults,
            value: booleanLabel,
        };
    }

    if (type === ProductAttributeType.option) {
        const localizedValues = getLocalizedAttributeValues(choices, value as string[]);
        const valueLabel = localizedValues.join(', ');

        return {
            ...attributeDefaults,
            value: valueLabel,
        };
    }

    let valueLabel = value.toString();

    if (sizeAttributes.includes(code as ProductDimensionAttributeKey)) {
        valueLabel = value
            ? transformToCentimeters(value as number)
            : '';
    }

    if (weightAttributes.includes(code as ProductDimensionAttributeKey)) {
        valueLabel = value
            ? transformToKilograms(value as number)
            : '';
    }

    if (code === ProductDimensionAttributeKey.volume) {
        valueLabel = value
            ? transformToMilliliters(value as number)
            : '';
    }

    if (code === ProductAttributeKey.wattage) {
        valueLabel = value
            ? transformToWattage(value as number)
            : '';
    }

    return {
        ...attributeDefaults,
        value: valueLabel,
    };
};
